/**
 * Personality Context
 * Manages financial personality type state with localStorage persistence
 * Supports QA mode for testing and future dashboard integration via postMessage
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { PersonalityTypeId } from '../data/personalities';
import { isAllowedOrigin, sendToParent } from '../lib/parentOrigins';

import { PERSONALITY_STORAGE_KEY } from './PersonalityContext.keys';
const STORAGE_KEY = PERSONALITY_STORAGE_KEY;

interface PersonalityContextType {
  /** Current personality type (null if not selected) */
  personalityType: PersonalityTypeId | null;
  /** Set the personality type */
  setPersonalityType: (type: PersonalityTypeId | null) => void;
  /** Whether QA mode is enabled (allows manual selection) */
  isQAMode: boolean;
  /** Clear the stored personality */
  clearPersonality: () => void;
}

const PersonalityContext = createContext<PersonalityContextType | undefined>(undefined);

export function PersonalityProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [personalityType, setPersonalityTypeState] = useState<PersonalityTypeId | null>(null);

  // QA mode: DISABLED - personality only comes from dashboard postMessage
  const isQAMode = false;

  // Load personality from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && isValidPersonalityType(stored)) {
        setPersonalityTypeState(stored as PersonalityTypeId);
      }
    } catch (error) {
      console.warn('Failed to load personality from localStorage:', error);
    }
  }, []);

  // Listen for personality data from parent window (future dashboard integration)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from exact whitelisted origins
      if (!isAllowedOrigin(event.origin)) {
        return;
      }

      // Handle personality data from dashboard
      if (event.data.type === 'PERSONALITY_DATA') {
        const receivedType = event.data.personalityType;
        if (receivedType && isValidPersonalityType(receivedType)) {
          setPersonalityType(receivedType as PersonalityTypeId);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Request personality data from parent on mount (in case we missed initial message)
    if (window.parent !== window) {
      sendToParent({ type: 'REQUEST_PERSONALITY_DATA' });
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const setPersonalityType = (type: PersonalityTypeId | null) => {
    setPersonalityTypeState(type);

    // Persist to localStorage
    try {
      if (type) {
        localStorage.setItem(STORAGE_KEY, type);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to save personality to localStorage:', error);
    }
  };

  const clearPersonality = () => {
    setPersonalityType(null);
  };

  // Clear stored personality on logout (token becomes null after clearAuth).
  // We ignore the very first render where the token may simply not have
  // arrived yet.
  const hadTokenRef = useTokenTransition(token);
  useEffect(() => {
    if (hadTokenRef && !token) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setPersonalityTypeState(null);
    }
  }, [hadTokenRef, token]);

  const value: PersonalityContextType = {
    personalityType,
    setPersonalityType,
    isQAMode,
    clearPersonality
  };

  return (
    <PersonalityContext.Provider value={value}>
      {children}
    </PersonalityContext.Provider>
  );
}

export function usePersonality() {
  const context = useContext(PersonalityContext);
  if (context === undefined) {
    throw new Error('usePersonality must be used within a PersonalityProvider');
  }
  return context;
}

/**
 * Returns true iff we previously had a non-null token. Used to detect the
 * logged-in -> logged-out transition so we only nuke personality data on
 * actual logout rather than first-render.
 */
function useTokenTransition(token: string | null): boolean {
  const hadTokenRef = useRef(false);
  useEffect(() => {
    if (token) hadTokenRef.current = true;
  }, [token]);
  return hadTokenRef.current;
}

/**
 * Validate that a string is a valid PersonalityTypeId
 */
function isValidPersonalityType(type: string): type is PersonalityTypeId {
  const validTypes: PersonalityTypeId[] = [
    'planner',
    'careful',
    'spontaneous',
    'explorer',
    'go-getter',
    'team-player'
  ];
  return validTypes.includes(type as PersonalityTypeId);
}
